import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/PageHeader";
import { timeAgo, dateStr } from "@/lib/format";
import {
  Robot, User, Globe, ShieldCheck, ListChecks, Warning, MagnifyingGlass,
  EnvelopeSimple, Star, ChatCircleDots, Funnel, FileText, Lightning, SignIn,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const catMeta = {
  auth: { icon: SignIn, color: "text-slate-600 bg-slate-100 border-slate-200" },
  segment: { icon: Funnel, color: "text-cyan-600 bg-cyan-50 border-cyan-200" },
  email: { icon: EnvelopeSimple, color: "text-primary bg-primary/10 border-primary/20" },
  automation: { icon: Lightning, color: "text-amber-600 bg-amber-50 border-amber-200" },
  template: { icon: FileText, color: "text-violet-600 bg-violet-50 border-violet-200" },
  reputation: { icon: Star, color: "text-yellow-600 bg-yellow-50 border-yellow-200" },
  chat: { icon: ChatCircleDots, color: "text-blue-600 bg-blue-50 border-blue-200" },
  system: { icon: Robot, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
};

const actorMeta = {
  user: { icon: User, label: "Staff", color: "text-foreground bg-secondary" },
  system: { icon: Robot, label: "System", color: "text-emerald-600 bg-emerald-50" },
  guest: { icon: Globe, label: "Guest", color: "text-blue-600 bg-blue-50" },
};

export default function AuditLog() {
  const [category, setCategory] = useState("all");
  const [actorType, setActorType] = useState("all");
  const [search, setSearch] = useState("");

  const { data: stats } = useQuery({ queryKey: ["audit-stats"], queryFn: async () => (await api.get("/audit/stats")).data });
  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["audit", category, actorType, search],
    queryFn: async () => (await api.get("/audit", { params: { category, actor_type: actorType, search } })).data,
    refetchInterval: 15000,
  });

  const kpis = [
    { icon: ListChecks, label: "Total events", value: stats?.total ?? 0, color: "bg-primary/10 text-primary" },
    { icon: User, label: "Staff actions", value: stats?.user ?? 0, color: "bg-secondary text-foreground" },
    { icon: Robot, label: "System actions", value: stats?.system ?? 0, color: "bg-emerald-50 text-emerald-600" },
    { icon: Warning, label: "Warnings", value: stats?.warnings ?? 0, color: "bg-amber-50 text-amber-600" },
  ];

  return (
    <Page>
      <PageHeader title="Audit Log" subtitle="Every change and automated action — who did what, and when.">
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2 w-2 rounded-full bg-success pulse-dot" /> Live</span>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {kpis.map((k, i) => (
          <div key={k.label} data-testid={`audit-stat-${i}`} className="rounded-lg border border-border bg-card p-4 flex items-center gap-3 fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <span className={`h-9 w-9 rounded-md flex items-center justify-center ${k.color}`}><k.icon size={17} weight="fill" /></span>
            <div><div className="font-heading text-xl font-semibold">{k.value}</div><div className="text-xs text-muted-foreground">{k.label}</div></div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="audit-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actions, targets, people…" className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-primary/30" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[160px] h-10" data-testid="audit-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            {["all", "auth", "segment", "email", "automation", "template", "reputation", "chat", "system"].map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c[0].toUpperCase() + c.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={actorType} onValueChange={setActorType}>
          <SelectTrigger className="w-[150px] h-10" data-testid="audit-filter-actor"><SelectValue placeholder="Actor" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actors</SelectItem>
            <SelectItem value="user">Staff</SelectItem>
            <SelectItem value="system">System</SelectItem>
            <SelectItem value="guest">Guest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {isLoading && <div className="px-4 py-10 text-center text-muted-foreground text-sm">Loading…</div>}
        {!isLoading && logs.length === 0 && <div className="px-4 py-10 text-center text-muted-foreground text-sm">No activity matches your filters.</div>}
        <div className="divide-y divide-border">
          {logs.map((l, i) => {
            const cat = catMeta[l.category] || catMeta.system;
            const actor = actorMeta[l.actor_type] || actorMeta.user;
            return (
              <div key={l.id} data-testid={`audit-row-${i}`} className="flex items-start gap-3.5 px-4 py-3.5 hover:bg-secondary/40 transition-colors">
                <span className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 border ${cat.color}`}><cat.icon size={17} weight="fill" /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-[12px] font-medium bg-secondary border border-border rounded px-1.5 py-0.5">{l.action}</code>
                    <span className="text-sm text-foreground truncate">{l.target}</span>
                    {l.status === "warning" && <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-600 flex items-center gap-1"><Warning size={11} weight="fill" /> attention</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-muted-foreground">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${actor.color}`}><actor.icon size={11} weight="fill" /> {l.actor_name}</span>
                    <span className={`px-1.5 py-0.5 rounded border capitalize ${cat.color}`}>{l.category}</span>
                    {l.details && Object.keys(l.details).length > 0 && (
                      <span className="text-muted-foreground/80 font-mono truncate max-w-[340px]">
                        {Object.entries(l.details).map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`).join("  ·  ")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-foreground">{timeAgo(l.timestamp)}</div>
                  <div className="text-[11px] text-muted-foreground">{dateStr(l.timestamp)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Page>
  );
}
